import {
  useId,
  useMemo,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";

import { Button } from "./Button";
import { matchesSubsequence } from "./CommandPalette";
import { InlineError, Spinner } from "./Feedback";
import { Heading, Text } from "./Typography";

// ScopePicker — the page BODY for a route that requires a scope the caller has
// not chosen yet (an agent, today). It replaces the dead-end "select an agent
// in the sidebar" empty state: the choices are ON the page, not somewhere else.
//
// The list renders IMMEDIATELY; the input filters an already-loaded list and is
// never a prerequisite for seeing options. Keyboard model matches
// `CommandPalette` (arrows move the highlight, Enter selects, the input keeps
// focus) — there is exactly one list-navigation interaction model in this app.
//
// Presentational only (UI-LAW §4): the feature layer owns the read, pagination,
// and what selection does.

export interface ScopePickerOption {
  id: string;
  label: string;
  // Optional secondary column (a short id, a team name). Never the identity.
  secondaryLabel?: string;
}

export interface ScopePickerProps {
  // Rendered instead of the search + list when the scope has NO options at all
  // (a zero-agent org). Callers give it a single CTA into the create flow.
  emptyState?: ReactNode;
  errorMessage?: string;
  hasNextPage?: boolean;
  // The destination surface's `ShellIcon`, rendered above the title.
  icon?: ReactNode;
  isFetchingNextPage?: boolean;
  isPending?: boolean;
  onLoadMore?: () => void;
  onSelect: (id: string) => void;
  options: ScopePickerOption[];
  // The thing being chosen, lowercase singular — "agent". Drives the subtitle
  // ("Choose an agent to continue") and the filter input.
  scopeNoun: string;
  // The destination surface, as the breadcrumb names it — "Simulations".
  surfaceLabel: string;
}

function article(noun: string): string {
  return /^[aeiou]/i.test(noun) ? "an" : "a";
}

export function ScopePicker({
  emptyState,
  errorMessage,
  hasNextPage = false,
  icon,
  isFetchingNextPage = false,
  isPending = false,
  onLoadMore,
  onSelect,
  options,
  scopeNoun,
  surfaceLabel,
}: ScopePickerProps) {
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [previousQuery, setPreviousQuery] = useState(query);
  const baseId = useId();
  const titleId = `${baseId}-title`;
  const listboxId = `${baseId}-listbox`;

  const matches = useMemo(
    () => options.filter((option) => matchesSubsequence(query, option.label)),
    [options, query],
  );

  if (previousQuery !== query) {
    setPreviousQuery(query);
    setActiveIndex(0);
  } else if (activeIndex >= matches.length && matches.length > 0) {
    setActiveIndex(matches.length - 1);
  }

  const activeDescendant =
    matches.length > 0
      ? `${listboxId}-option-${String(activeIndex)}`
      : undefined;

  const onInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (matches.length === 0) {
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((current) => (current + 1) % matches.length);
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex(
        (current) => (current - 1 + matches.length) % matches.length,
      );
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      const activeOption = matches[activeIndex];
      if (activeOption) {
        onSelect(activeOption.id);
      }
    }
  };

  const header = (
    <>
      {icon ? (
        <span aria-hidden="true" className="marker-scope-picker__icon">
          {icon}
        </span>
      ) : null}
      <Heading id={titleId} level={2} size="md">
        Continue to {surfaceLabel}
      </Heading>
      <Text variant="muted">
        Choose {article(scopeNoun)} {scopeNoun} to continue
      </Text>
    </>
  );

  // No options at all is a different problem from "none match the filter":
  // the caller's create-flow CTA replaces the picker entirely.
  if (!isPending && options.length === 0 && emptyState !== undefined) {
    return (
      <section aria-labelledby={titleId} className="marker-scope-picker">
        {header}
        {emptyState}
      </section>
    );
  }

  return (
    <section aria-labelledby={titleId} className="marker-scope-picker">
      {header}
      <div className="marker-scope-picker__search">
        <input
          aria-activedescendant={activeDescendant}
          aria-controls={listboxId}
          aria-expanded="true"
          aria-label={`Find ${scopeNoun}`}
          onChange={(event) => {
            setQuery(event.target.value);
          }}
          onKeyDown={onInputKeyDown}
          placeholder={`Find ${scopeNoun}…`}
          role="combobox"
          type="text"
          value={query}
        />
      </div>
      {errorMessage ? <InlineError>{errorMessage}</InlineError> : null}
      {isPending ? (
        <div className="marker-scope-picker__state">
          <Spinner label={`Loading ${scopeNoun}s`} />
          <span>Loading {scopeNoun}s…</span>
        </div>
      ) : null}
      <div
        aria-label={`${surfaceLabel} ${scopeNoun} options`}
        className="marker-scope-picker__listbox"
        id={listboxId}
        role="listbox"
      >
        {matches.map((option, index) => {
          const isActive = index === activeIndex;
          return (
            <button
              aria-selected={isActive}
              className={[
                "marker-scope-picker__row",
                isActive ? "marker-scope-picker__row--active" : "",
              ].join(" ")}
              id={`${listboxId}-option-${String(index)}`}
              key={option.id}
              onClick={() => {
                onSelect(option.id);
              }}
              onMouseEnter={() => {
                setActiveIndex(index);
              }}
              role="option"
              tabIndex={-1}
              type="button"
            >
              <span
                className="marker-scope-picker__row-label"
                title={option.label}
              >
                {option.label}
              </span>
              {option.secondaryLabel === undefined ? null : (
                <span className="marker-scope-picker__row-meta">
                  {option.secondaryLabel}
                </span>
              )}
            </button>
          );
        })}
      </div>
      {!isPending && matches.length === 0 && options.length > 0 ? (
        <Text variant="muted">No {scopeNoun} matches that filter.</Text>
      ) : null}
      {hasNextPage && onLoadMore ? (
        <Button
          disabled={isFetchingNextPage}
          type="button"
          variant="ghost"
          onClick={onLoadMore}
        >
          {isFetchingNextPage ? "Loading…" : `Load more ${scopeNoun}s`}
        </Button>
      ) : null}
    </section>
  );
}
